import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save } from "lucide-react";
import { catalogSnapshot, CAT_GROUPS as DEFAULT_GROUPS } from "@/lib/kalkulatorCatalog";
import useKalkulatorPalette from "@/hooks/useKalkulatorPalette";

const badgeColors = ["std", "dlx", "lux", "oth"];
const slugify = (s) => "cat_" + s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/(^_|_$)/g, "").slice(0, 24) + "_" + Math.random().toString(36).slice(2, 6);

// Every row here stacks label+delete on one line and Min/Maks on the next, using flex-1 +
// min-w-0 throughout instead of fixed pixel widths (w-32 etc). A fixed-width row is what
// used to overflow the page horizontally on a ~375–430px phone screen — five/six fixed-width
// controls in one line simply don't fit, and because nothing wrapped that overflow in its
// own scroll container, the WHOLE PAGE scrolled sideways instead (the "berantakan" the user
// hit). Stacking two short lines instead means every control just shrinks to fit its half of
// the screen — no scrolling needed at any width, phone or desktop alike.
function PriceRow({ pal, inp, inpS, labelValue, onLabel, labelPlaceholder, mn, mx, onMn, onMx, onDelete, deleteTitle }) {
  return (
    <div className="rounded-lg p-2" style={{ background: pal.cardHd, border: `1px solid ${pal.borderSoft}` }}>
      <div className="flex items-center gap-2">
        <input className={inp + " flex-1 min-w-0"} style={inpS} value={labelValue} placeholder={labelPlaceholder} onChange={onLabel} />
        <button onClick={onDelete} title={deleteTitle} className="shrink-0 hover:text-red-400 p-1" style={{ color: pal.faint }}><Trash2 size={14} /></button>
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-[10px] shrink-0 w-8" style={{ color: pal.muted }}>Min</span>
        <input type="number" className={inp + " flex-1 min-w-0"} style={inpS} value={mn} onChange={onMn} />
        <span className="text-[10px] shrink-0 w-9 text-right" style={{ color: pal.muted }}>Maks</span>
        <input type="number" className={inp + " flex-1 min-w-0"} style={inpS} value={mx} onChange={onMx} />
      </div>
    </div>
  );
}

// Single dashboard for every mechanism's price data — Mekanisme 1's per-category catalog,
// Mekanisme 2's universal tiers, and the shared Lantai/Top Table/Backsplash material list.
// Saving here PUTs to /settings/kalkulator-catalog, which every calculator instance reads
// on load (see useKalkulatorCatalog) — so this is the single source of truth the request
// asked for: edit once, every mechanism and every open calculator picks it up.
//
// Colors come from useKalkulatorPalette() (same tokens the builder dialog uses) rather than
// hardcoded dark Tailwind values — this page used to stay black even when the app was in
// light mode, since a `html.light` stylesheet rule can never beat a literal class/style.
export default function KalkulatorMasterData() {
  const pal = useKalkulatorPalette();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["kalkulator-catalog-admin"], queryFn: async () => (await api.get("/settings/kalkulator-catalog")).data });
  const [categories, setCategories] = useState(null);
  const [groups, setGroups] = useState(null);
  const [tiers2, setTiers2] = useState(null);
  const [lantai, setLantai] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    const seed = data && Object.keys(data).length ? data : catalogSnapshot();
    setCategories(JSON.parse(JSON.stringify(seed.categories || catalogSnapshot().categories)));
    setGroups(JSON.parse(JSON.stringify(seed.groups && seed.groups.length ? seed.groups : DEFAULT_GROUPS)));
    setTiers2(JSON.parse(JSON.stringify(seed.tiers2 && seed.tiers2.length ? seed.tiers2 : catalogSnapshot().tiers2)));
    setLantai(JSON.parse(JSON.stringify(seed.lantai || catalogSnapshot().lantai)));
  }, [isLoading, data]);

  const inp = "w-full rounded-lg text-xs px-2 py-1.5 focus:outline-none";
  const inpS = { background: pal.input, border: `1px solid ${pal.inputBorder}`, color: pal.text };
  const ghostBtn = "text-xs px-3 py-1.5 rounded-full flex items-center gap-1 shrink-0";
  const ghostBtnS = { background: pal.ghostBtnBg, border: `1px solid ${pal.ghostBtnBorder}`, color: pal.ghostBtnText };

  if (isLoading || !categories) return <div className="text-center py-10 text-sm" style={{ color: pal.faint }}>Memuat master data…</div>;

  const catKeys = Object.keys(categories).filter((k) => k !== "" && k !== "manual");

  const setCat = (key, field, val) => setCategories({ ...categories, [key]: { ...categories[key], [field]: val } });
  const setKelas = (key, idx, field, val) => {
    const g = categories[key].g.map((row, i) => i === idx ? { ...row, [field]: field === "mn" || field === "mx" ? Number(val) || 0 : val } : row);
    setCat(key, "g", g);
  };
  const addKelas = (key) => setCat(key, "g", [...(categories[key].g || []), { k: "Kelas Baru", mn: 0, mx: 0, b: badgeColors[(categories[key].g?.length || 0) % 4] }]);
  const delKelas = (key, idx) => setCat(key, "g", categories[key].g.filter((_, i) => i !== idx));
  const addCategory = () => {
    const label = window.prompt("Nama kategori baru (mis. \"Kabinet Pantry\"):");
    if (!label?.trim()) return;
    const key = slugify(label);
    setCategories({ ...categories, [key]: { label: label.trim(), g: [{ k: "ESSENTIAL", mn: 0, mx: 0, b: "std" }] } });
    const lastGroupName = groups[groups.length - 1]?.[0] === "Lainnya" ? "Lainnya" : null;
    if (lastGroupName) setGroups(groups.map((g, i) => i === groups.length - 1 ? [g[0], [...g[1], key]] : g));
    else setGroups([...groups, ["Lainnya", [key]]]);
  };
  const delCategory = (key) => {
    if (!window.confirm(`Hapus kategori "${categories[key].label}"? Dokumen lama yang sudah memakai kategori ini tidak berubah (datanya sudah tersimpan sendiri di dokumennya), tapi kategori ini tidak akan bisa dipilih lagi untuk dokumen baru.`)) return;
    const { [key]: _drop, ...rest } = categories;
    setCategories(rest);
    setGroups(groups.map(([g, ks]) => [g, ks.filter((k) => k !== key)]).filter(([, ks]) => ks.length));
  };

  const setTier = (idx, field, val) => setTiers2(tiers2.map((t, i) => i === idx ? { ...t, [field]: field === "mn" || field === "mx" ? Number(val) || 0 : val } : t));
  const addTier = () => setTiers2([...tiers2, { k: "TIER BARU", mn: 0, mx: 0, b: badgeColors[tiers2.length % 4] }]);
  const delTier = (idx) => tiers2.length > 1 && setTiers2(tiers2.filter((_, i) => i !== idx));

  const setLantaiRow = (key, field, val) => setLantai({ ...lantai, [key]: { ...lantai[key], [field]: field === "mn" || field === "mx" ? Number(val) || 0 : val } });
  const addLantaiRow = () => {
    const label = window.prompt("Nama material baru:");
    if (!label?.trim()) return;
    setLantai({ ...lantai, [slugify(label)]: { label: label.trim(), mn: 0, mx: 0 } });
  };
  const delLantaiRow = (key) => { const { [key]: _drop, ...rest } = lantai; setLantai(rest); };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/kalkulator-catalog", { categories, groups, tiers2, lantai });
      toast.success("Master data RAB Kalkulator disimpan — langsung berlaku untuk semua kalkulator.");
      qc.invalidateQueries({ queryKey: ["kalkulator-catalog"] });
      qc.invalidateQueries({ queryKey: ["kalkulator-catalog-admin"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 min-w-0">
      <div className="rounded-xl px-3 py-2 text-xs" style={{ background: pal.warnBg, border: `1px solid ${pal.warnBorder}`, color: pal.warnText }}>
        Semua harga di sini otomatis dipakai oleh Kalkulator Estimasi di setiap lead (Mekanisme 1 &amp; 2). Dokumen kalkulator yang sudah dibuat tidak berubah — angka di sana sudah tersimpan sendiri saat dibuat.
      </div>

      {/* MEKANISME 2 TIERS */}
      <div className="rounded-2xl p-4 min-w-0" style={{ background: pal.card, border: `1px solid ${pal.border}` }}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-sm" style={{ color: pal.text }}>Mekanisme 2 — Tier Universal (Per Ruangan)</h3>
            <p className="text-xs mt-0.5" style={{ color: pal.muted }}>Berlaku untuk semua uraian manual di Mekanisme 2, tidak tergantung kategori.</p>
          </div>
          <button onClick={addTier} className={ghostBtn} style={ghostBtnS}><Plus size={12} />Tier</button>
        </div>
        <div className="space-y-2">
          {tiers2.map((t, i) => (
            <PriceRow key={i} pal={pal} inp={inp} inpS={inpS}
              labelValue={t.k} onLabel={(e) => setTier(i, "k", e.target.value)} labelPlaceholder="Nama tier"
              mn={t.mn} mx={t.mx} onMn={(e) => setTier(i, "mn", e.target.value)} onMx={(e) => setTier(i, "mx", e.target.value)}
              onDelete={() => delTier(i)} deleteTitle="Hapus tier" />
          ))}
        </div>
      </div>

      {/* MEKANISME 1 CATEGORIES */}
      <div className="rounded-2xl p-4 min-w-0" style={{ background: pal.card, border: `1px solid ${pal.border}` }}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-sm" style={{ color: pal.text }}>Mekanisme 1 — Kategori &amp; Kelas Katalog</h3>
            <p className="text-xs mt-0.5" style={{ color: pal.muted }}>Tiap kategori punya beberapa kelas (mis. ESSENTIAL/SIGNATURE) dengan rentang harga sendiri.</p>
          </div>
          <button onClick={addCategory} className={ghostBtn} style={ghostBtnS}><Plus size={12} />Kategori</button>
        </div>
        <div className="space-y-3">
          {catKeys.map((key) => (
            <div key={key} className="rounded-xl overflow-hidden min-w-0" style={{ border: `1px solid ${pal.border}`, background: pal.dialogBg }}>
              <div className="flex items-center gap-2 px-3 py-2" style={{ background: pal.cardHd, borderBottom: `1px solid ${pal.border}` }}>
                <input className={inp + " flex-1 min-w-0"} style={inpS} value={categories[key].label} onChange={(e) => setCat(key, "label", e.target.value)} />
                <button onClick={() => addKelas(key)} className="text-[11px] px-2 py-1 rounded-md flex items-center gap-1 shrink-0" style={ghostBtnS}><Plus size={11} />Kelas</button>
                <button onClick={() => delCategory(key)} className="shrink-0 hover:text-red-400 p-1" style={{ color: pal.faint }}><Trash2 size={14} /></button>
              </div>
              <div className="p-2.5 space-y-1.5">
                {(categories[key].g || []).map((row, i) => (
                  <PriceRow key={i} pal={pal} inp={inp} inpS={inpS}
                    labelValue={row.k} onLabel={(e) => setKelas(key, i, "k", e.target.value)}
                    mn={row.mn} mx={row.mx} onMn={(e) => setKelas(key, i, "mn", e.target.value)} onMx={(e) => setKelas(key, i, "mx", e.target.value)}
                    onDelete={() => delKelas(key, i)} deleteTitle="Hapus kelas" />
                ))}
                {!(categories[key].g || []).length && <p className="text-[11px]" style={{ color: pal.faint }}>Belum ada kelas — tambahkan minimal 1.</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* LANTAI CATALOG */}
      <div className="rounded-2xl p-4 min-w-0" style={{ background: pal.card, border: `1px solid ${pal.border}` }}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="font-heading font-semibold text-sm min-w-0" style={{ color: pal.text }}>Lantai · Top Table · Backsplash — Material</h3>
          <button onClick={addLantaiRow} className={ghostBtn} style={ghostBtnS}><Plus size={12} />Material</button>
        </div>
        <div className="space-y-1.5">
          {Object.entries(lantai).filter(([k]) => k !== "" && k !== "lantai_manual").map(([key, row]) => (
            <PriceRow key={key} pal={pal} inp={inp} inpS={inpS}
              labelValue={row.label} onLabel={(e) => setLantaiRow(key, "label", e.target.value)}
              mn={row.mn} mx={row.mx} onMn={(e) => setLantaiRow(key, "mn", e.target.value)} onMx={(e) => setLantaiRow(key, "mx", e.target.value)}
              onDelete={() => delLantaiRow(key)} deleteTitle="Hapus material" />
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={busy} className="w-full sm:w-auto border-0 font-semibold" style={{ background: pal.silverBtnGrad, color: pal.silverBtnText }}>
        {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />}Simpan Master Data
      </Button>
    </div>
  );
}
